<div class="card">
  <h3>Documents</h3>
  <p class="p-mb-0">Document template engines and generation volume.</p>

  <div class="p-grid p-mt-3" [formGroup]="filterForm">
    <div class="p-col-12 p-md-4">
      <label>Filter by</label>
      <p-dropdown
        [options]="periodTypes"
        formControlName="periodType"
        styleClass="full-width"
        appendTo="body"
      ></p-dropdown>
    </div>
    <div class="p-col-12 p-md-4">
      <label>Period</label>
      <p-dropdown
        [options]="periods"
        optionLabel="name"
        optionValue="value"
        formControlName="period"
        styleClass="full-width"
        appendTo="body"
      ></p-dropdown>
    </div>
    <div class="p-col-12 p-md-4">
      <label>Dates</label>
      <p-calendar
        formControlName="customPeriod"
        selectionMode="range"
        [readonlyInput]="true"
        styleClass="full-width"
        appendTo="body"
      ></p-calendar>
    </div>
  </div>
</div>

<div class="card">
  <p-table [value]="rows" [loading]="loading" responsiveLayout="scroll">
    <ng-template pTemplate="header">
      <tr>
        <th>Document type</th>
        <th>Document name</th>
        <th>Active engine</th>
        <th>PandaDoc template ID</th>
        <th>Aegis template URL</th>
        <th class="numeric">PandaDoc documents</th>
        <th class="numeric">Aegis documents</th>
        <th class="numeric">Aegis migration rate</th>
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-row>
      <tr>
        <td>{{ row.docType }}</td>
        <td>{{ row.name }}</td>
        <td>
          <span [pTooltip]="engineSwitchTooltip(row)" tooltipPosition="top">
            <button
              type="button"
              class="engine-toggle"
              [disabled]="!!engineSwitchDisabledReason(row)"
              [attr.aria-label]="'Switch from ' + row.engine + ' document engine'"
              (click)="switchEngine(row)"
            >
              <p-tag
                [value]="row.engine"
                [severity]="row.engine === 'aegis' ? 'success' : 'info'"
                [icon]="updatingEngineKeys.has(row.config.key) ? 'pi pi-spin pi-spinner' : 'pi pi-arrows-v'"
              ></p-tag>
            </button>
          </span>
        </td>
        <td>
          <p-inputGroup *ngIf="isEditing(row.config.key, 'templateId'); else pandadocDisplay">
            <input pInputText [(ngModel)]="editableValue" [disabled]="saving" (keydown)="handleEditorKeydown($event, row, 'templateId')">
            <button pButton type="button" icon="pi pi-times" class="p-button-outlined p-button-danger" [disabled]="saving" (click)="cancelEditing()"></button>
            <button pButton type="button" [icon]="saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'" class="p-button-outlined" [disabled]="saving" (click)="save(row, 'templateId')"></button>
          </p-inputGroup>
          <ng-template #pandadocDisplay>
            <span class="editable-link">
              <a *ngIf="row.pandadocTemplateUrl; else noPandadocTemplate" [href]="row.pandadocTemplateUrl" target="_blank" rel="noopener noreferrer">{{ row.pandadocTemplateId }}</a>
              <ng-template #noPandadocTemplate>—</ng-template>
              <button pButton type="button" icon="pi pi-pencil" class="p-button-text p-button-sm" aria-label="Edit PandaDoc template ID" (click)="startEditing(row, 'templateId')"></button>
            </span>
          </ng-template>
        </td>
        <td class="url">
          <div *ngIf="isEditing(row.config.key, 'templateUrl'); else aegisDisplay" class="template-editor">
            <p-autoComplete
              [(ngModel)]="editableValue"
              [suggestions]="filteredAegisTemplates"
              (completeMethod)="filterAegisTemplates($event)"
              [dropdown]="true"
              [completeOnFocus]="true"
              [forceSelection]="false"
              [disabled]="saving"
              appendTo="body"
              placeholder="Search templates"
              styleClass="template-autocomplete"
            >
              <ng-template let-templateUrl pTemplate="item">
                {{ aegisTemplateName(templateUrl) }}
              </ng-template>
            </p-autoComplete>
            <button pButton type="button" icon="pi pi-times" class="p-button-outlined p-button-danger" [disabled]="saving" (click)="cancelEditing()"></button>
            <button pButton type="button" [icon]="saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'" class="p-button-outlined" [disabled]="saving" (click)="save(row, 'templateUrl')"></button>
          </div>
          <ng-template #aegisDisplay>
            <span class="editable-link">
              <a
                *ngIf="row.aegisTemplateUrl; else noAegisTemplate"
                [href]="row.aegisTemplateUrl"
                target="_blank"
                rel="noopener noreferrer"
              >{{ aegisTemplateName(row.aegisTemplateUrl) }}</a>
              <ng-template #noAegisTemplate>—</ng-template>
              <button pButton type="button" icon="pi pi-pencil" class="p-button-text p-button-sm" aria-label="Edit Aegis template URL" (click)="startEditing(row, 'templateUrl')"></button>
            </span>
          </ng-template>
        </td>
        <td class="numeric">{{ documentCount(row, 'pandadoc') }}</td>
        <td class="numeric">{{ documentCount(row, 'aegis') }}</td>
        <td class="numeric">
          <ng-container *ngIf="aegisMigrationRate(row) !== null; else noMigrationRate">
            {{ aegisMigrationRate(row) | percent:'1.1-1' }}
          </ng-container>
          <ng-template #noMigrationRate>—</ng-template>
        </td>
      </tr>
    </ng-template>
    <ng-template pTemplate="footer">
      <tr *ngIf="rows.length" class="summary-row">
        <td colspan="5">Total</td>
        <td class="numeric">{{ totalDocumentCount('pandadoc') }}</td>
        <td class="numeric">{{ totalDocumentCount('aegis') }}</td>
        <td class="numeric">
          <ng-container *ngIf="totalAegisMigrationRate() !== null; else noTotalMigrationRate">
            {{ totalAegisMigrationRate() | percent:'1.1-1' }}
          </ng-container>
          <ng-template #noTotalMigrationRate>—</ng-template>
        </td>
      </tr>
    </ng-template>
    <ng-template pTemplate="emptymessage">
      <tr><td colspan="8">No standard document configurations found.</td></tr>
    </ng-template>
  </p-table>
</div>
